import React, { useState, useEffect } from "react";
import {
  TextField,
  InputAdornment,
  Box,
  Typography,
  Modal,
  Button,
  Autocomplete,
  Grid,
  Drawer,
  useMediaQuery,
  Hidden,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogActions
} from "@mui/material";
import { LoadScript, GoogleMap, MarkerF } from "@react-google-maps/api";
import {
  DeleteOutline,
  HomeRounded,
  LocationOnOutlined,
  MyLocation,
  Place,
  SearchSharp,
  WorkRounded
} from "@mui/icons-material";
import Images from "../../utils/Images";
import axios from "axios";
import { API, GOOGLE_MAP } from "../../utils/Api";
import { getPreciseDistance } from "geolib";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setPickedAddress } from "../../store/Address/addressSlice";
import DeliveryOutletScreen from "./DeliveryScreens/DeliveryOutletScreen";
import toast from "react-hot-toast";
import PlacesAutocomplete from "../../components/PlacesComponent";

/* =======================
   Google Geocoder helpers
   ======================= */
const geocoderRef = { current: null };

function ensureGeocoder() {
  if (!geocoderRef.current) {
    // window.google is available after <LoadScript> loads
    geocoderRef.current = new window.google.maps.Geocoder();
  }
  return geocoderRef.current;
}

// Map Google's result into your existing "Nominatim-like" shape
function googleToOsmShape(geoResult, lat, lng) {
  const comp = (type) =>
    geoResult.address_components.find((c) => c.types.includes(type))?.long_name || "";

  return {
    address: {
      suburb:
        comp("sublocality") ||
        comp("sublocality_level_1") ||
        comp("neighborhood"),
      village: "",
      town: comp("locality"),
      county: comp("administrative_area_level_2"),
      state_district: comp("administrative_area_level_2"),
      state: comp("administrative_area_level_1"),
      postcode: comp("postal_code")
    },
    display_name: geoResult.formatted_address,
    lat,
    lon: lng
  };
}

async function reverseGeocodeGoogle(lat, lng) {
  const geocoder = ensureGeocoder();
  return new Promise((resolve, reject) => {
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results && results.length) {
        resolve(googleToOsmShape(results[0], lat, lng));
      } else {
        reject(new Error(`Geocode failed: ${status}`));
      }
    });
  });
}

const DeliveryScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [map, setMap] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [locationDrawer, setLocationDrawer] = useState(true);
  const [cities, setCities] = useState([]);
  const token = localStorage.getItem("token");
  const [selectedCity, setSelectedCity] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [selectedCityData, setSelectedCityData] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [address, setAddress] = useState([]);
  const [markerPosition, setMarkerPosition] = useState({
    lat: 20.5937,
    lng: 78.9629
  });
  const [zoom, setZoom] = useState(5);
  const [savedUserAddressList, setSavedUserAddressList] = useState([]);
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);
  const matchesMdUp = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const [drawerForDeliveryOutlet, setDrawerForDeliveryOutlet] = useState(false);
  const [addressModal, setAddressModal] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Smooth pan + zoom helper (kept your behavior)
  const animateTo = (mapObj, coords, targetZoom = 14) => {
    if (!mapObj) return;
    mapObj.panTo(coords);
    let currentZoom = mapObj.getZoom();
    const id = setInterval(() => {
      if (currentZoom < targetZoom) {
        currentZoom += 1;
        mapObj.setZoom(currentZoom);
      } else {
        clearInterval(id);
      }
    }, 200);
  };

  const handleCoordinatesChange = async (newCoordinates) => {
    setZoom(5);
    setMarkerPosition(newCoordinates);
    try {
      const data = await reverseGeocodeGoogle(
        newCoordinates.lat,
        newCoordinates.lng
      );
      setAddress(data);
      dispatch(setPickedAddress(data));
      localStorage.setItem("pickedAddress", JSON.stringify(data));
    } catch (error) {
      console.error("Reverse geocode failed:", error);
      toast.error("Unable to fetch address for this location.");
    } finally {
      setLoading(false);
    }
    if (map) {
      animateTo(map, newCoordinates, 14);
    }
  };

  const containerStyle = {
    width: "100%",
    height: matchesMdUp ? "55vh" : "75vh"
  };

  const onLoad = (map) => {
    setMap(map);
    setMapLoaded(true);
    setMarkerPosition({ lat: 20.5937, lng: 78.9629 });
  };

  const fetchLocation = async () => {
    try {
      handleCloseDrawer();
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });
      const { latitude, longitude } = position.coords;
      setLatitude(latitude);
      setLongitude(longitude);
      const coords = { lat: latitude, lng: longitude };
      setMarkerPosition(coords);
      localStorage.setItem("deliveryLatitude", latitude);
      localStorage.setItem("deliveryLongitude", longitude);

      if (map) {
        animateTo(map, coords, 14);
      }

      // ---- REPLACED NOMINATIM WITH GOOGLE GEOCODER ----
      const data = await reverseGeocodeGoogle(latitude, longitude);
      setAddress(data);
      dispatch(setPickedAddress(data));
      localStorage.setItem("pickedAddress", JSON.stringify(data));
    } catch (error) {
      console.error("Error getting current location:", error);
      // Keep your existing UX
      if (error.code === error.PERMISSION_DENIED) {
        const allowed = window.confirm(
          "Location permission is required. Do you want to allow it?"
        );
        if (allowed) {
          fetchLocation();
        } else {
          window.prompt("Location permission is required to proceed.");
        }
      } else {
        toast.error("Unable to fetch your current location.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddress = () => {
    if (matchesMdUp) {
      setDrawerForDeliveryOutlet(true);
    } else {
      navigate("/deliveryOutletScreen");
    }
    localStorage.setItem("selectedDeliveryAddressType", "NEW");
  };

  const handleCloseModal = () => setModalOpen(false);
  const handleOpenDrawer = () => setLocationDrawer(true);
  const handleCloseDrawer = () => setLocationDrawer(false);

  const handleCloseDrawerForDeliveryOutlet = () => {
    const selectedStoreData = localStorage.getItem("selectedStoreData");
    if (!selectedStoreData) return;
    setDrawerForDeliveryOutlet(false);
  };

  const handleSearchChange = (event) => setSearchValue(event.target.value);

  const handleMarkerDragEnd = async (event) => {
    const newLat = event.latLng.lat();
    const newLng = event.latLng.lng();
    setMarkerPosition({ lat: newLat, lng: newLng });
    setLatitude(newLat);
    setLongitude(newLng);
    localStorage.setItem("deliveryLatitude", newLat);
    localStorage.setItem("deliveryLongitude", newLng);

    // ---- REPLACED NOMINATIM WITH GOOGLE GEOCODER ----
    try {
      const data = await reverseGeocodeGoogle(newLat, newLng);
      console.log("====================================");
      console.log(data);
      console.log("====================================");
      setAddress(data);
      localStorage.setItem("pickedAddress", JSON.stringify(data));
      dispatch(setPickedAddress(data));
    } catch (e) {
      console.error("Reverse geocode failed:", e);
      toast.error("Unable to fetch address for the selected point.");
    }
  };

  const fetchUserAddresses = async () => {
    try {
      const response = await axios.get(`${API.BASE_URL_2}delivery-details/user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedUserAddressList(response?.data?.data?.deliveryDetails);
    } catch (error) {
      console.log("Saved user address Error", error?.response?.data);
    }
  };

  useEffect(() => {
    fetchUserAddresses();
    try {
      if ("permissions" in navigator) {
        navigator.permissions.query({ name: "geolocation" }).then((result) => {
          if (result.state === "granted") setIsPermissionGranted(true);
          else setIsPermissionGranted(false);
        });
      } else {
        setIsPermissionGranted(false);
      }
    } catch {
      setIsPermissionGranted(false);
    }
  }, []);

  const handleSelectedAddressClick = (address) => {
    localStorage.setItem("selectedAddress", JSON.stringify(address));
    localStorage.setItem("selectedDeliveryAddressType", "EXISTING");
    if (matchesMdUp) {
      setLocationDrawer(true);
      setDrawerForDeliveryOutlet(true);
    } else {
      navigate("/deliveryOutletScreen");
    }
    setAddressModal(false);
    handleDialogClose();
    toast.success("Address selected successfully");
  };

  const handleAddressModalOpen = () => setAddressModal(true);
  const handleAddressModalClose = () => setAddressModal(false);

  const handleDeleteClick = (addressId) => {
    setAddressToDelete(addressId);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setAddressToDelete(null);
  };

  const handleViewAddress = () => {
    if (matchesMdUp) {
      handleAddressModalOpen();
    } else {
      // mobile handling (kept)
      console.log("kashndfj");
    }
  };

  const deleteAddress = async (addressId) => {
    try {
      const response = await axios.delete(`${API.BASE_URL_2}delivery-details`, {
        data: { _id: addressId },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Address deleted successfully");
      fetchUserAddresses();
    } catch (error) {
      console.log("Delete address Error", error?.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = () => {
    if (addressToDelete) {
      deleteAddress(addressToDelete);
    }
    handleDialogClose();
  };

  return (
    <LoadScript
      googleMapsApiKey={GOOGLE_MAP.GOOGLE_MAP_KEY}
      // If your Places component already loads libraries itself, you can omit:
      // libraries={["places"]}
      loadingElement={
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            flexDirection: "column",
            textAlign: "center",
            backgroundColor: "#eaeaea"
          }}
        >
          <LocationOnOutlined sx={{ fontSize: 50 }} />
          <Typography variant="h6">
            Picking Up <br />
            Location
          </Typography>
        </Box>
      }
    >
      <>
        <Box className="searchCityBox" sx={{ left: "12%" }}>
          {/* Autocomplete is already handled by you */}
          <PlacesAutocomplete onCoordinatesChange={handleCoordinatesChange} />
        </Box>

        <GoogleMap
          mapContainerStyle={containerStyle}
          center={markerPosition}
          zoom={zoom}
          onLoad={onLoad}
          options={{
            mapTypeControl: false,
            zoomControl: false,
            fullscreenControl: false,
            keyboardShortcuts: false,
            streetViewControl: false
          }}
        >
          {markerPosition.lat && markerPosition.lng && mapLoaded && (
            <MarkerF
              position={markerPosition}
              draggable={true}
              onDragEnd={handleMarkerDragEnd}
              icon={Images.xpanseDeliveryPin}
            />
          )}
        </GoogleMap>

        <Box className="locateMeButton" onClick={fetchLocation}>
          <Place /> Locate Me
        </Box>

        {address && address.address ? (
          <Grid container className="address-container">
            <Grid item xs={9}>
              <Typography className="address">
                <MyLocation sx={{ paddingRight: "5px" }} />
                {address.address.suburb ||
                  address.address.village ||
                  address.address.town ||
                  address.address.county}
              </Typography>
              <Typography className="address2">
                {/* {address.address.state_district}, {address.address.state},{" "}
                {address.address.postcode}. */}
                {address.display_name}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box
                className="button"
                sx={{ backgroundColor: "red", cursor: "pointer" }}
                onClick={handleAddress}
              >
                Continue
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Grid
            container
            className="address-container"
            sx={{ padding: "30px", textAlign: "center" }}
          >
            <Typography
              variant="body1"
              sx={{ textAlign: "center", color: "#186B8C", fontWeight: "700" }}
            >
              Move the pin to your location
            </Typography>
          </Grid>
        )}

        <Drawer open={locationDrawer} onClose={handleCloseDrawer} anchor="bottom">
          {isPermissionGranted ? null : (
            <Box
              sx={{
                backgroundColor: "#F9F0E7",
                borderTopLeftRadius: "12px",
                borderTopRightRadius: "12px"
              }}
            >
              <Grid container justifyContent={"space-evenly"}>
                <Grid>
                  <MyLocation className="locationIcon" />
                </Grid>
                <Grid className="permissionTitle">Location Permission Required</Grid>
                <Grid sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                  <Box className="grantButton" onClick={fetchLocation}>
                    Grant
                  </Box>
                </Grid>
                <Grid>
                  <Typography className="titleDesc">
                    Granting Location permission will ensure accurate address and hassle
                    free delivery
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {savedUserAddressList.length > 0 && (
            <Box className="addressSelectBox">
              <Typography sx={{ fontWeight: "700" }}>Select Delivery Address</Typography>
              <Typography
                onClick={handleViewAddress}
                sx={{
                  color: "#1E9CED",
                  textDecoration: "underline",
                  fontWeight: "700",
                  cursor: "pointer"
                }}
              >
                VIEW ALL
              </Typography>
            </Box>
          )}

          {savedUserAddressList.length > 0 &&
            savedUserAddressList.slice(0, 2).map((address, index) => (
              <Box
                key={address._id}
                onClick={() => handleSelectedAddressClick(address)}
                className="addressBox"
                sx={{
                  borderBottom:
                    savedUserAddressList.length === 1 ||
                    index === savedUserAddressList.length - 1
                      ? "none"
                      : "1px solid #c5c5c5",
                  margin: "10px 20px 0px 20px",
                  cursor: "pointer"
                }}
              >
                <Box sx={{ display: "flex", padding: "0 0 0 0px" }}>
                  {address.type === "HOME" && <HomeRounded sx={{ color: "#1E9CED" }} />}
                  {address.type === "WORK" && <WorkRounded sx={{ color: "#1E9CED" }} />}
                  {address.type !== "HOME" &&
                    address.type !== "WORK" && (
                      <LocationOnOutlined sx={{ color: "#1E9CED" }} />
                    )}
                  <Typography sx={{ fontWeight: "700", padding: "0 0 0 10px" }}>
                    {address.receiverName}
                  </Typography>
                </Box>
                <Typography sx={{ padding: "0 10px 15px 30px" }}>
                  {address?.addressLine1}, {address?.addressLine2}, {address?.landmark},{" "}
                  {address?.state}, {address?.pincode}
                </Typography>
              </Box>
            ))}

          <Box
            onClick={handleCloseDrawer}
            sx={{
              border: "1px solid #09A5F7",
              margin: "15px 15px 18px 15px",
              borderRadius: "12px",
              cursor: "pointer"
            }}
          >
            <Hidden mdUp>
              <Typography sx={{ color: "#BEBEBE", padding: "16px" }}>
                Enter Location
              </Typography>
            </Hidden>
            <Hidden mdDown>
              <Typography sx={{ color: "#BEBEBE", padding: "16px" }}>
                Select Different Location
              </Typography>
            </Hidden>
          </Box>
        </Drawer>

        <Drawer
          anchor="left"
          open={drawerForDeliveryOutlet}
          onClose={handleCloseDrawerForDeliveryOutlet}
          PaperProps={{
            sx: {
              height: "100%",
              overflow: "auto",
              "&::-webkit-scrollbar": { display: "none" },
              "-ms-overflow-style": "none",
              "scrollbar-width": "none"
            }
          }}
        >
          <Box sx={{ overflow: "auto" }}>
            <DeliveryOutletScreen />
          </Box>
        </Drawer>
      </>
      <Modal open={modalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            boxShadow: 0,
            padding: "10px 30px 10px 30px",
            borderRadius: "12px",
            textAlign: "center"
          }}
        >
          <Typography variant="h6" component="div" gutterBottom>
            No outlets found for your city.
          </Typography>
          <Button onClick={handleCloseModal}>Continue</Button>
        </Box>
      </Modal>

      <Dialog
        open={addressModal}
        onClose={handleAddressModalClose}
        fullWidth
        sx={{ "& .MuiPaper-root": { backgroundColor: "#DFEEF4", borderRadius: "16px" } }}
      >
        <Box className="savedAddressesScreen">
          {savedUserAddressList.map((address, index) => (
            <Box
              onClick={() => handleSelectedAddressClick(address)}
              key={address._id}
              className="addressBox"
              sx={{
                borderBottom:
                  savedUserAddressList.length === 1 ||
                  index === savedUserAddressList.length - 1
                    ? "none"
                    : "1px solid #c5c5c5",
                margin: "10px 20px 0px 20px",
                cursor: "pointer"
              }}
            >
              <Box sx={{ display: "flex", padding: "0 0 0 0px" }}>
                {address.type === "HOME" && <HomeRounded sx={{ color: "#1E9CED" }} />}
                {address.type === "WORK" && <WorkRounded sx={{ color: "#1E9CED" }} />}
                {address.type !== "HOME" &&
                  address.type !== "WORK" && (
                    <LocationOnOutlined sx={{ color: "#1E9CED" }} />
                  )}
                <Typography sx={{ fontWeight: "700", padding: "0 0 0 10px" }}>
                  {address.receiverName}
                </Typography>
              </Box>
              <Typography sx={{ padding: "0 10px 15px 30px" }}>
                {address?.addressLine1}, {address?.addressLine2}, {address?.landmark},{" "}
                {address?.state}, {address?.pincode}
              </Typography>
            </Box>
          ))}
        </Box>
      </Dialog>

      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Confirm Address Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this address?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="primary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <style>
        {`
          body, html {
            margin: 0;
            padding: 0;
            background-color: #DFEEF4;
          }
        `}
      </style>
    </LoadScript>
  );
};

export default DeliveryScreen;
